/**
 * ユーザーメニューとメニュー内モーダルの開閉連動（タスク39）。
 *
 * 各操作コントロール（チェックイン/プロフィール/ステータス/投稿）のモーダルは UserMenu の
 * 展開パネル内に描かれるため、項目クリック時にメニューを閉じるとモーダルごとアンマウントされる。
 * そこで「モーダルが閉じたらメニューも閉じる」を、各コントロールの open state を
 * useMenuModal() に置き換えるだけで実現する（閉じる経路＝成功後の自動クローズ・背景・閉じるボタンを問わない）。
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export interface UserMenuContextValue {
  /** メニュー内モーダルの開閉を UserMenu に伝える（false でメニューも閉じる）。 */
  readonly setModalOpen: (open: boolean) => void;
}

export const UserMenuContext = createContext<UserMenuContextValue | null>(null);

/**
 * メニュー内モーダル用の open state（`useState(false)` の置き換え）。
 * UserMenu の外で使われた場合（ctx なし）は通知せず通常の state として振る舞う。
 */
export function useMenuModal(): [boolean, (open: boolean) => void] {
  const ctx = useContext(UserMenuContext);
  const [open, setOpenState] = useState(false);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setOpen = useCallback(
    (next: boolean) => {
      // アンマウント後の呼び出し（成功後 1.5 秒の setTimeout が、先に手動で閉じた後に発火する等）は捨てる。
      // 通知すると、開き直したメニューを勝手に閉じてしまうため。
      if (!mountedRef.current) {
        return;
      }
      setOpenState(next);
      ctx?.setModalOpen(next);
    },
    [ctx],
  );

  return [open, setOpen];
}
